/** A friendly volcano: green-skirted cone, glowing crater, pastel smoke puffs. */
import type { VolcanoLayer } from '@dropstack/core';
import type { SceneSize } from '../layout.ts';
import { valueNoise } from '../noise.ts';
import { circlePath, ellipsePath, gloss, glow, rgba, rimOf, shade, smoothLine, strokeRim, vGradient, type Ctx, type Point } from './draw.ts';
import type { Painter } from './types.ts';

interface Cone {
  cx: number;
  base: number;
  top: number;
  halfBase: number;
  halfTop: number;
}

function coneOf(layer: VolcanoLayer, size: SceneSize): Cone {
  const base = layer.baseline * size.height;
  const h = layer.height * size.unit;
  const halfBase = (layer.width * size.unit) / 2;
  return { cx: layer.x * size.width, base, top: base - h, halfBase, halfTop: halfBase * 0.2 };
}

/** Concave flank from the crater lip down to the base (t = 0 at the top). */
function flank(c: Cone, side: -1 | 1, t: number): Point {
  const spread = c.halfTop + (c.halfBase - c.halfTop) * t ** 1.6;
  return { x: c.cx + side * spread, y: c.top + (c.base - c.top) * t };
}

/** Outline of the cone. With `closed` false it is just the flanks and crater lip (for the rim: no line along the foot). */
function conePath(ctx: Ctx, c: Cone, seed: number, closed = true): void {
  const left: Point[] = [];
  const right: Point[] = [];
  for (let i = 0; i <= 16; i++) {
    const t = i / 16;
    const wob = (c.base - c.top) * 0.012 * valueNoise(seed, i * 0.7);
    const l = flank(c, -1, t);
    const r = flank(c, 1, t);
    left.push({ x: l.x + wob, y: l.y });
    right.push({ x: r.x - wob, y: r.y });
  }
  ctx.beginPath();
  smoothLine(ctx, [...left].reverse());
  // Crater lip: a soft dip between the two shoulders.
  ctx.quadraticCurveTo(c.cx, c.top + (c.base - c.top) * 0.04, c.cx + c.halfTop, c.top);
  smoothLine(ctx, right, true);
  if (closed) ctx.closePath();
}

/** The beach ellipse at the foot of an island volcano. */
function shoreOf(c: Cone): { cx: number; cy: number; rx: number; ry: number } {
  const h = c.base - c.top;
  return { cx: c.cx, cy: c.base + h * 0.012, rx: c.halfBase * 1.1, ry: h * 0.06 };
}

/** Sandy beach wrapped around the foot, with a line of surf where it meets the sea. */
function paintShore(ctx: Ctx, c: Cone, shore: string, size: SceneSize): void {
  const b = shoreOf(c);
  ctx.beginPath();
  ellipsePath(ctx, b.cx, b.cy, b.rx, b.ry);
  ctx.fillStyle = vGradient(ctx, b.cy - b.ry, b.cy + b.ry, [shade(shore, 0.12), shore, shade(shore, -0.08)]);
  ctx.fill();
  // Darker sand where the beach meets the jungle (upper half only).
  ctx.beginPath();
  ctx.ellipse(b.cx, b.cy, b.rx, b.ry, 0, Math.PI * 1.04, Math.PI * 1.96);
  strokeRim(ctx, rgba(shade(shore, -0.28), 0.75), size, 0.8);
  // Surf along the seaward edge (lower half).
  ctx.beginPath();
  ctx.ellipse(b.cx, b.cy, b.rx, b.ry, 0, Math.PI * 0.02, Math.PI * 0.98);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.lineWidth = Math.max(1.5, b.ry * 0.28);
  ctx.lineCap = 'round';
  ctx.stroke();
}

export const volcanoPainter: Painter<VolcanoLayer> = {
  still(ctx, layer, { size, seed }) {
    const c = coneOf(layer, size);
    const h = c.base - c.top;
    conePath(ctx, c, seed);
    ctx.fillStyle = vGradient(ctx, c.top, c.base, [shade(layer.color, 0.12), layer.color, shade(layer.color, -0.06)]);
    ctx.fill();

    ctx.save();
    conePath(ctx, c, seed);
    ctx.clip();
    // Lava rivulets trickling from the crater.
    for (const side of [-1, 1] as const) {
      const pts: Point[] = [];
      for (let i = 0; i <= 8; i++) {
        const t = i / 8;
        pts.push({ x: c.cx + side * (c.halfTop * 0.5 + h * 0.08 * t) + h * 0.025 * Math.sin(t * 7 + side), y: c.top + h * 0.45 * t });
      }
      ctx.beginPath();
      smoothLine(ctx, pts);
      ctx.lineCap = 'round';
      ctx.strokeStyle = layer.lava;
      ctx.lineWidth = h * 0.05;
      ctx.stroke();
      ctx.strokeStyle = rgba(shade(layer.lava, 0.5), 0.9);
      ctx.lineWidth = h * 0.018;
      ctx.stroke();
    }
    // Jungle skirt on the lower slopes, with a bumpy tree-line top.
    if (layer.skirt) {
      const line = c.top + h * 0.55;
      ctx.beginPath();
      ctx.moveTo(c.cx - c.halfBase - 10, c.base + 2);
      for (let i = 0; i <= 24; i++) {
        const x = c.cx - c.halfBase + (2 * c.halfBase * i) / 24;
        const d = Math.abs(x - c.cx) / c.halfBase;
        const y = line + h * 0.3 * d * d - h * 0.04 * Math.abs(Math.sin(i * 1.9 + seed));
        ctx.lineTo(x, y);
      }
      ctx.lineTo(c.cx + c.halfBase + 10, c.base + 2);
      ctx.closePath();
      ctx.fillStyle = vGradient(ctx, line, c.base, [shade(layer.skirt, 0.1), layer.skirt, shade(layer.skirt, -0.1)]);
      ctx.fill();
      strokeRim(ctx, rimOf(layer.skirt), size, 0.8);
    }
    ctx.restore();

    conePath(ctx, c, seed, false);
    strokeRim(ctx, layer.rim ?? rimOf(layer.color), size);
    if (layer.shore) paintShore(ctx, c, layer.shore, size);

    // Crater mouth.
    ctx.beginPath();
    ellipsePath(ctx, c.cx, c.top + h * 0.012, c.halfTop * 0.85, h * 0.03);
    ctx.fillStyle = layer.lava;
    ctx.fill();
    strokeRim(ctx, shade(layer.lava, -0.3), size, 0.8);
    gloss(ctx, c.cx - c.halfBase * 0.35, c.top + h * 0.35, h * 0.05, h * 0.16, 0.18, 0.5);
  },

  moving(ctx, layer, { size, time }) {
    const c = coneOf(layer, size);
    const h = c.base - c.top;
    if (layer.shore) {
      // A soft ring of surf spreads out from the beach and fades, every six seconds.
      const b = shoreOf(c);
      const k = (time / 6) % 1;
      ctx.beginPath();
      ctx.ellipse(b.cx, b.cy, b.rx * (1.02 + 0.07 * k), b.ry * (1.1 + 0.9 * k), 0, Math.PI * 0.02, Math.PI * 0.98);
      ctx.strokeStyle = `rgba(255, 255, 255, ${(0.7 * (1 - k)).toFixed(3)})`;
      ctx.lineWidth = Math.max(1, b.ry * 0.16);
      ctx.lineCap = 'round';
      ctx.stroke();
    }
    glow(ctx, c.cx, c.top, h * (0.2 + 0.03 * Math.sin(time * 1.3)), layer.lava, 0.45);
    // Smoke puffs rise, swell and fade on a slow 9-second cycle.
    const puffs = 5;
    for (let i = 0; i < puffs; i++) {
      const age = (time / 9 + i / puffs) % 1;
      const r = h * (0.05 + 0.1 * age);
      const x = c.cx + h * 0.12 * Math.sin(age * 3 + i) + h * 0.2 * age;
      const y = c.top - h * (0.06 + 0.55 * age);
      const alpha = Math.sin(age * Math.PI) * 0.85;
      ctx.beginPath();
      circlePath(ctx, x, y, r);
      circlePath(ctx, x + r * 0.7, y + r * 0.2, r * 0.7);
      circlePath(ctx, x - r * 0.65, y + r * 0.25, r * 0.6);
      ctx.fillStyle = rgba(layer.smoke, alpha);
      ctx.fill();
      ctx.beginPath();
      circlePath(ctx, x - r * 0.25, y - r * 0.3, r * 0.35);
      ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.5})`;
      ctx.fill();
    }
  },
};
